import axios from 'axios';
import { ApiResponse, ServerInfo, IpmiInfo } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const serverApi = {
  // Получить список серверов
  getServers: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get<ApiResponse<string[]>>('/server/list');
    return response.data;
  },

  // Получить информацию о сервере
  getServerInfo: async (serviceName: string): Promise<ApiResponse<ServerInfo>> => {
    const response = await api.get<ApiResponse<ServerInfo>>(`/server/${serviceName}`);
    return response.data;
  },

  // Перезагрузить сервер
  rebootServer: async (serviceName: string, type: 'soft' | 'hard' = 'soft'): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/server/${serviceName}/reboot`, { type });
    return response.data;
  },

  // Получить доступные функции
  getFeatures: async (serviceName: string): Promise<ApiResponse<string[]>> => {
    const response = await api.get<ApiResponse<string[]>>(`/server/${serviceName}/features`);
    return response.data;
  },

  // Обновить настройки мониторинга
  updateMonitoring: async (serviceName: string, monitoring: boolean, noIntervention: boolean): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/server/${serviceName}/monitoring`, {
      monitoring,
      noIntervention
    });
    return response.data;
  },
};

export const ipmiApi = {
  // Получить информацию о IPMI функции
  getIpmiFeatures: async (serviceName: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/server/${serviceName}/features/ipmi`);
    return response.data;
  },

  // Получить доступ к IPMI
  getIpmiAccess: async (serviceName: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/ipmi/${serviceName}/access`);
    return response.data;
  },

  // Активировать IPMI доступ
  activateIpmiAccess: async (serviceName: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/ipmi/${serviceName}/activate`);
    return response.data;
  },

  // Получить статус IPMI
  getIpmiStatus: async (serviceName: string): Promise<ApiResponse<IpmiInfo>> => {
    const response = await api.get<ApiResponse<IpmiInfo>>(`/ipmi/${serviceName}/status`);
    return response.data;
  },

  // Получить доступные типы IPMI доступа
  getIpmiAccessTypes: async (serviceName: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/ipmi/${serviceName}/access-types`);
    return response.data;
  },

  // Получить HTML5 URL для KVM over IP
  getKvmHtml5Url: async (serviceName: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/ipmi/${serviceName}/html5-url`);
    return response.data;
  },

  // Запросить доступ к IPMI
  requestIpmiAccess: async (serviceName: string, ipAddress?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/ipmi/${serviceName}/request-access`, {
      ipAddress: ipAddress || undefined
    });
    return response.data;
  },

  // Получить прокси URL для HTML5 KVM
  getKvmProxyUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/proxy-html5`;
  },

  // Получить прямой URL для HTML5 KVM
  getKvmDirectUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/direct-kvm`;
  },

  // Получить redirect URL для HTML5 KVM
  getKvmRedirectUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/kvm-redirect`;
  },

  // Получить launcher URL для HTML5 KVM через прокси
  getKvmLauncherUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/kvm-launcher`;
  },

  // Получить auto-kvm URL для автоматической активации и запуска KVM
  getAutoKvmUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/auto-kvm`;
  },

  // Получить diagnose URL для диагностики IPMI
  getDiagnoseUrl: (serviceName: string): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/ipmi/${serviceName}/diagnose`;
  },
};

export const authApi = {
  // Login
  login: async (username: string, password: string): Promise<ApiResponse<{ username: string }>> => {
    const response = await api.post<ApiResponse<{ username: string }>>('/auth/login', {
      username,
      password
    });
    return response.data;
  },

  // Logout
  logout: async (): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/logout');
    return response.data;
  },

  // Check authentication status
  checkAuth: async (): Promise<ApiResponse<{ authenticated: boolean; username?: string }>> => {
    const response = await api.get<ApiResponse<{ authenticated: boolean; username?: string }>>('/auth/check');
    return response.data;
  },
};

export default api;
